import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { Cross } from "components/icons/Cross";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { BankConnection } from "types/Models/banks";
import { useHistory } from "react-router-dom";
import BillCom from "static/images/BillCom.svg";
import { Divider } from "components/design/Divider";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import ThreeDots from "static/images/ThreeDots.svg";
import { useModal } from "hooks/useModal";
import { DisconnectBankModal } from "components/Connections/DisconnectBankModal";
import { ACCOUNT_CONNECTION_TYPE } from "constants/dataSource";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { pluralize } from "utils/pluralize";

export const BillComSlider = ({
  connection,
  onClose,
}: {
  connection: BankConnection;
  onClose: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const disconnectModalState = useModal();

  const { isFetching, data: apArConnections } = useGetEntityBanksQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
      type: ACCOUNT_CONNECTION_TYPE.AP_AR,
    },
    { skip: !groupId || !entityId }
  );

  const firstItem = apArConnections?.items?.[0];
  const billCount = firstItem?.bill_count ?? 0;
  const vendorCount = firstItem?.vendor_count ?? 0;
  const customerCount = firstItem?.customer_count ?? 0;
  const invoiceCount = firstItem?.invoice_count ?? 0;

  const visibleItems = [
    billCount > 0 && "bills",
    vendorCount > 0 && "vendors",
    customerCount > 0 && "customers",
    invoiceCount > 0 && "invoices",
  ].filter(Boolean);

  const isLastItem = (itemType: string) =>
    visibleItems[visibleItems.length - 1] === itemType;

  return (
    <Slider.Root onOpenChange={onClose} open={Boolean(connection)}>
      <Slider.Content>
        {connection && (
          <div className="t-flex t-flex-col t-h-full">
            <div className="t-flex t-px-5 py-3 t-border-0 t-items-center t-border-b t-border-solid t-border-neutral-0 t-sticky t-top-0 t-z-10 t-bg-surface t-flex-shrink-0">
              <img src={BillCom} alt="" />
              <p className="t-m-0 t-ml-2">{connection?.name}</p>
              <div className="t-flex t-ml-auto">
                {connection.is_connection_available && (
                  <Dropdown.Root>
                    <Dropdown.Trigger asChild>
                      <div>
                        <Button
                          customType="ghost_icon"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img src={ThreeDots} alt="" />
                        </Button>
                      </div>
                    </Dropdown.Trigger>
                    <Dropdown.Content align="end">
                      <Dropdown.Item
                        onSelect={disconnectModalState.open}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <p className="t-m-0">Disconnect</p>
                      </Dropdown.Item>
                    </Dropdown.Content>
                  </Dropdown.Root>
                )}
                <Button
                  customType="ghost_icon"
                  onClick={onClose}
                  isLoading={isFetching}
                  disabled={isFetching}
                >
                  <Cross color="currentColor" />
                </Button>
              </div>
            </div>

            <Slider.Body>
              <Accordion.Root type="multiple" defaultValue={["summary"]}>
                <Accordion.Item value="summary">
                  <Accordion.Trigger>Summary</Accordion.Trigger>
                  <Accordion.Content>
                    <div className="t-flex t-flex-col t-gap-6 t-p-4">
                      {billCount > 0 && (
                        <>
                          <div className="t-flex t-justify-between t-items-center">
                            <span className=" t-text-body-sm">
                              {pluralize(billCount, "Bill", "Bills")}
                            </span>
                            <Button
                              customType="link_neutral"
                              size="small"
                              onClick={() =>
                                history.push(link("/books/bills-and-vendors"))
                              }
                            >
                              View
                            </Button>
                          </div>
                          {!isLastItem("bills") && <Divider />}
                        </>
                      )}
                      {vendorCount > 0 && (
                        <>
                          <div className="t-flex t-justify-between t-items-center">
                            <span className=" t-text-body-sm">
                              {pluralize(vendorCount, "Vendor", "Vendors")}
                            </span>
                            <Button
                              customType="link_neutral"
                              size="small"
                              onClick={() =>
                                history.push(link("/books/expenses/vendors"))
                              }
                            >
                              View
                            </Button>
                          </div>
                          {!isLastItem("vendors") && <Divider />}
                        </>
                      )}
                      {customerCount > 0 && (
                        <>
                          <div className="t-flex t-justify-between t-items-center">
                            <span className=" t-text-body-sm">
                              {pluralize(
                                customerCount,
                                "Customer",
                                "Customers"
                              )}
                            </span>
                            <Button
                              customType="link_neutral"
                              size="small"
                              onClick={() =>
                                history.push(link("/books/revenue/customers"))
                              }
                            >
                              View
                            </Button>
                          </div>
                          {!isLastItem("customers") && <Divider />}
                        </>
                      )}
                      {invoiceCount > 0 && (
                        <>
                          <div className="t-flex t-justify-between t-items-center">
                            <span className=" t-text-body-sm">
                              {pluralize(invoiceCount, "Invoice", "Invoices")}
                            </span>
                            <Button
                              customType="link_neutral"
                              size="small"
                              onClick={() =>
                                history.push(
                                  link("/books/invoices-and-customers")
                                )
                              }
                            >
                              View
                            </Button>
                          </div>
                          {!isLastItem("invoices") && <Divider />}
                        </>
                      )}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion.Root>
            </Slider.Body>
          </div>
        )}
        <DisconnectBankModal
          show={disconnectModalState.isOpen}
          onClose={() => {
            disconnectModalState.close();
          }}
          connection={connection}
        />
      </Slider.Content>
    </Slider.Root>
  );
};
